<template lang="pug">
div
  div.text-sm.font-semibold.neutral700--text Appointment {{ apptSession.index + 1 }}
  v-row.mt-1(dense align="center")
    //- Details
    v-col(cols="4")
      div.text-sm.neutral700--text {{ servicesName }}
      .d-flex.align-center.mt-1
        v-avatar.mr-1(color="neutral300" size="16" rounded="circle")
          v-img(v-if="apptSession.provider.profile_image" width="16" height="16" :src="apptSession.provider.profile_image.file")
          v-icon(v-else size="16") $yzi-user-03
        div.text-xs {{ apptSession.provider.name }}
    //- Middle item
    v-col
      span.text-sm.round-sm.px-2.py-1(v-if="!isPending" :class="[`${status.textColor}--text`, status.bgColor]") 
        v-icon.mr-1.pb-1(size="13" :color="status.textColor") {{ status.icon }}
        span {{ status.text }}
    v-spacer
    //- Date
    v-col(cols="2")
      set-appointment-date(v-model="selectedDate" placeholder="Select date" hide-details="true" :required="false" :loading="isFetchingDates" :picker-date.sync="pickerDate" :min-date="getMinDate()" :unavailable-dates="unavailableDates" :disabled="!isPending" @change="fetchTimes(selectedDate, true)")
    //- Time
    v-col(cols="2")
      y-select(v-model="selectedTime" :items="times" item-text="text" item-value="value" placeholder="Select time" :rules="!selectedDate ? [] : rules.not_empty_select" :loading="isFetchingTimes" :disabled="!selectedDate || !isPending" :hide-details="true" no-data-text="No timeslot available" @change="setTime(selectedTime)")
    //- Actions
    v-col(cols="1")
      v-menu(v-if="!isPending && apptSession.status !== 'received'" offset-x top rounded="lg")
        template(#activator="{ on }")
          v-btn.ml-2(icon small v-on="on")
            v-icon mdi-dots-horizontal
        v-list.py-0
          template(v-for="(action, actidx) in actionList")
            v-divider(v-if="actidx !== 0")
            v-list-item(@click="action.action()" :key="'action-' + actidx")
              v-list-item-title.text-sm(:class="[action.textColor]") {{ action.text }}
  .dialogs
    reschedule-date(v-if="openRescheduleDateDialog" :open.sync="openRescheduleDateDialog" :selectedAppointment="apptSession" @reset="$emit('refetch')")
    cancel-appointment(v-if="openCancelAppointmentDialog" :open.sync="openCancelAppointmentDialog" :selectedAppointment="apptSession" @reset="$emit('refetch')")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { DateTime } from 'luxon'
import SetAppointmentDate from '@/components/clinic/appointment/advance/SetAppointmentDate.vue'
import RescheduleDate from '@/components/clinic/dialogs/RescheduleDate.vue'
import CancelAppointment from '@/components/clinic/dialogs/CancelAppointment.vue'

export default {
  components: { SetAppointmentDate, RescheduleDate, CancelAppointment },
  props: {
    session: {
      required: true,
      type: Object,
      default: (() => {})
    },
    hideCancelled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isFetchingDates: false,
      isFetchingTimes: false,
      setLater: false,
      selectedDate: null,
      selectedTime: null,
      pickerDate: null,
      unavailableDates: [],
      times: [],
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 1|| `Amount should be more than ${this.$currency.symbol} 1`],
        not_empty_select: [v => !!v || 'This field is required']
      },
      actions: [
        { id: 'reschedule', text: this.$t('reschedule_new_date'), action: () => (this.openRescheduleDate()), textColor: 'neutral700--text' },
        { id: 'cancel', text: this.$t('cancel_this_appointment'), action: () => (this.openCancelAppointment()), textColor: 'danger500--text' },
      ],
      selectedAppointment: null,
      openRescheduleDateDialog: false,
      openCancelAppointmentDialog: false
    }
  },
  watch: {
    pickerDate (val) {
      this.fetchUnavailableDates(val)
    }
  },
  mounted () {
    this.setData()
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      isHq: 'hq/getIsHq',
      hqSelectedCompany: 'hq/getSelectedCompany'
    }),
    apptSession: {
      get () {
        return this.session
      },
      set (val) {
        this.$emit('update:session', val)
      }
    },
    servicesName () {
      return this.apptSession?.services.map(service => service.name).join(', ')
    },
    isPending () {
      return this.apptSession?.status === 'pending'
    },
    status () {
      const statusMapper = {
        'new': {
          text: 'Upcoming',
          textColor: 'success500',
          bgColor: 'success100',
          icon: '$yzi-calendar'
        },
        'processed': {
          text: 'Upcoming',
          textColor: 'success500',
          bgColor: 'success100',
          icon: '$yzi-calendar'
        },
        'received': {
          text: 'Completed',
          textColor: 'info500',
          bgColor: 'info100',
          icon: '$yzi-check'
        },
        'absent': {
          text: 'Missed',
          textColor: 'danger500',
          bgColor: 'danger100',
          icon: '$yzi-alert-circle'
        },
        'cancelled': {
          text: 'Cancel',
          textColor: 'warning500',
          bgColor: 'warning100',
          icon: '$yzi-slash-circle-01'
        },
      }

      return statusMapper[this.apptSession?.status] || {}
    },
    actionList () {
      const defaultAction = ['reschedule', 'cancel']
      const cancelled = ['reschedule']
      if (this.hideCancelled) { return cancelled.map(m => { return { ...this.actions.find(f => f.id === m) } })  }
      if (this.apptSession.status === 'new' || this.apptSession.status === 'processed' || this.apptSession.status === 'asbent') { return defaultAction.map(m => { return { ...this.actions.find(f => f.id === m) } }) }
      if (this.apptSession.status === 'cancelled') { return cancelled.map(m => { return { ...this.actions.find(f => f.id === m) } }) }
      return this.actions
    },
    companySlug () {
      return this.isHq ? this.hqSelectedCompany.slug : this.company.slug
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    async setData () {
      if (this.apptSession) {
        if (this.apptSession.start) {
          this.selectedDate = this.apptSession.start.substr(0, 10)
          const time = DateTime.fromISO(this.apptSession.start)
          this.selectedTime = time.toFormat('HH:mm:ss')
          const timeData = {
            text: time.toFormat('h:mm a'),
            value: this.selectedTime, 
          }
          this.times = [timeData]
        } else if (this.apptSession.preset_date) {
          await this.fetchTimes(this.apptSession.preset_date)
          this.selectedDate = this.apptSession.preset_date
        }
      }
    },
    fetchUnavailableDates (yearMonth) {
      this.times = []
      this.isFetchingDates = true
      let params = { services: this.apptSession.services.map(m => m.id).join(), provider: this.apptSession.provider.id }
      if (yearMonth) {
        params.year = yearMonth.substr(0, 4),
        params.month = yearMonth.substr(-2)
      }
      this.$axios.get(`/clinics/${this.companySlug}/block_dates`, { params })
        .then(resp => {
          this.unavailableDates = resp.data
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.isFetchingDates = false
        })
    },
    fetchTimes (date, clearTime) {
      if (clearTime) {
        this.setDate(date)
      }
      this.isFetchingTimes = true
      let params = {
        services: this.apptSession.services.map(m => m.id).join() || null,
        date: date || null
      }
      return this.$axios.get(`/clinics/${this.companySlug}/providers/${this.apptSession.provider.id}/available_times`, { params })
        .then((resp) => {
          this.times = resp.data.times.map(m => { return { text: this.$options.filters.displayTime2(m), value: m } })
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.isFetchingTimes = false
        })
    },
    getMinDate () {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`;
    },
    setDate (date) {
      this.selectedTime = null
      if (date) {
        this.$set(this.apptSession, 'preset_date', date)
      } else {
        this.$set(this.apptSession, 'start', null)
        this.$set(this.apptSession, 'preset_date', null)
      }
    },
    async setTime (time) {
      const start = `${this.selectedDate} ${time}`
      try {
        this.isFetchingTimes = true
        const data = {
          start,
        }
        const endpoint = `/clinics/${this.companySlug}/appointments/${this.apptSession.id}`
        const resp = await this.$axios.patch(endpoint, data)
        this.$set(this.apptSession, 'start', start)
        this.$set(this.apptSession, 'preset_date', null)
        this.$set(this.apptSession, 'set_later', false)
        this.updateActivityLog(endpoint, data, resp, 'PATCH', this.apptSession.id)
        this.showMessage(['success', 'Appointment successfully booked and updated'])
        this.$emit('refetch')
      } catch (err) {
        let message = this.$t('error_message.unable_to_complete_request')
        if (err.response?.data?.detail) {
          message = err.response.data.detail
        }
        this.showMessage(['error', message])
        this.$set(this.apptSession, 'start', null)
        this.selectedTime = null
      } finally {
        this.isFetchingTimes = false
      }
    },
    openRescheduleDate () {
      this.openRescheduleDateDialog = true
    },
    openCancelAppointment () {
      this.openCancelAppointmentDialog = true
    },
    updateActivityLog (endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'appointment',
        activity: 'Set time for to be scheduled recurring appointment',
        raw_data: { endpoint, payload, response: response.data, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
</style>